/**
 * Eine Apartmentzeile: sticky Bezeichnung, Tagesraster und die Buchungsbalken darüber. Gewählt
 * werden kann jeder Tag ab heute, auch über Buchungen hinweg.
 */
import type { AvailabilityDistributionApartment } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { diffIsoDays, isWeekendIso } from './calendarDates';
import { gridColumns, ROW_HEIGHT, ROW_HEIGHT_SELECTED } from './calendarGeometry';
import { ApartmentRowLabel } from './ApartmentRowLabel';
import { useAutomationLayer } from '../automation/AutomationLayerContext';
import { ownedCell } from '../automation/automationModel';
import { BookingBar } from './BookingBar';
import { DayCell } from './DayCell';
import { SelectionOverlay } from './SelectionOverlay';
import type { ApartmentModel } from './calendarModel';
import { rangeIndexAt, type SelectedRange, type SelectionMode } from './rangeSelectionModel';

interface ApartmentRowProps {
  apartment: AvailabilityDistributionApartment;
  model: ApartmentModel;
  today: string;
  sidebarWidth: number;
  dayMin: number;
  selection: readonly SelectedRange[];
  /** Zimmer eines Verbunds unter seiner Gesamtwohnung. */
  child?: boolean;
  onOpenMonth?: (propertyId: string) => void;
  onBegin?: (propertyId: string, date: string, mode: SelectionMode, immediate?: boolean) => void;
  onExtend?: (date: string) => void;
}

export function ApartmentRow({
  apartment,
  model,
  today,
  sidebarWidth,
  dayMin,
  selection,
  child,
  onOpenMonth,
  onBegin,
  onExtend,
}: ApartmentRowProps) {
  const covered = new Set(model.bars.flatMap((bar) => coveredIndices(bar.startIndex, bar.nights)));
  const twoLanes = model.bars.some((bar) => bar.lane > 0);
  const layer = useAutomationLayer();
  return (
    <div
      className={cn(
        'border-card-alt grid border-b',
        selection.length > 0 ? 'bg-forest-soft/40' : 'bg-card',
      )}
      style={{
        gridTemplateColumns: gridColumns(sidebarWidth, model.dates.length, dayMin),
        gridTemplateRows: `${selection.length > 0 ? ROW_HEIGHT_SELECTED : ROW_HEIGHT}px`,
      }}
    >
      <ApartmentRowLabel
        propertyId={apartment.propertyId}
        name={apartment.name}
        operations={apartment.operations}
        child={child}
        mode={layer?.mode(apartment.propertyId) ?? null}
        onOpenMonth={onOpenMonth}
      />
      {model.cells.map((cell) => (
        <DayCell
          key={cell.day.date}
          cell={cell}
          currency={apartment.currency}
          isToday={cell.day.date === today}
          isWeekend={isWeekendIso(cell.day.date)}
          covered={covered.has(cell.index)}
          selectionIndex={rangeIndexAt(selection, cell.day.date)}
          automation={ownedCell(layer?.cell(apartment.propertyId, cell.day.date) ?? null, cell.day)}
          onBegin={
            onBegin && cell.day.date >= today
              ? (date, mode, immediate) => onBegin(apartment.propertyId, date, mode, immediate)
              : undefined
          }
          onExtend={onExtend ? (date) => date >= today && onExtend(date) : undefined}
        />
      ))}
      {model.bars.map((bar) => (
        <BookingBar key={bar.key} bar={bar} gridStart={bar.startIndex + 2} twoLanes={twoLanes} />
      ))}
      {selection.map((range) => {
        const placement = placeRange(range, model.dates);
        return placement ? <SelectionOverlay key={range.id} {...placement} /> : null;
      })}
    </div>
  );
}

function coveredIndices(start: number, length: number): number[] {
  return Array.from({ length }, (_, offset) => start + offset);
}

/** Ein Zeitraum darf ueber das sichtbare Fenster hinausgehen; gerahmt wird nur sein sichtbarer Teil. */
function placeRange(
  range: SelectedRange,
  dates: string[],
): { gridStart: number; span: number } | null {
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (!first || !last || range.to < first || range.from > last) return null;
  const from = range.from < first ? first : range.from;
  const to = range.to > last ? last : range.to;
  return { gridStart: diffIsoDays(first, from) + 2, span: diffIsoDays(from, to) + 1 };
}
